import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, addDoc, query, orderBy, onSnapshot, serverTimestamp, doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from '../context/UserContext';
import { IoSend, IoChatbubble, IoHeart, IoHeartOutline } from 'react-icons/io5';
import './Comments.css';

export default function Comments({ bookId, chapterId, bookAuthorId, bookTitle }) {
  const { user } = useUser();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [sortBy, setSortBy] = useState('recent');

  const commentsPath = `books/${bookId}/chapters/${chapterId}/comments`;

  useEffect(() => {
    if (!bookId || !chapterId) return;
    const q = query(collection(db, commentsPath), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setComments(data);
    });
    return () => unsubscribe();
  }, [bookId, chapterId, commentsPath]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user || !text.trim() || sending) return;
    setSending(true);

    await addDoc(collection(db, commentsPath), {
      text: text.trim(),
      authorName: user.name,
      authorId: user.id,
      likes: 0,
      likedBy: [],
      bookId: bookId,
      replyTo: replyTo || null,
      replyToName: replyTo ? comments.find(c => c.id === replyTo)?.authorName || '' : '',
      createdAt: serverTimestamp(),
    });

    if (bookAuthorId && bookAuthorId !== user.id) {
      await addDoc(collection(db, 'notifications'), {
        recipientId: bookAuthorId,
        fromName: user.name,
        fromId: user.id,
        bookId: bookId,
        bookTitle: bookTitle,
        type: 'comment',
        read: false,
        createdAt: serverTimestamp(),
      });
    }

    addDoc(collection(db, 'activities'), {
      type: 'comment',
      userId: user.id,
      userName: user.name,
      bookId: bookId,
      bookTitle: bookTitle,
      createdAt: serverTimestamp(),
    });

    setText('');
    setReplyTo(null);
    setSending(false);
  };

  const handleLikeComment = async (comment) => {
    if (!user) { window.location.href = '/auth'; return; }
    const commentRef = doc(db, commentsPath, comment.id);
    const likedBy = comment.likedBy || [];
    const hasLiked = likedBy.includes(user.id);
    if (hasLiked) {
      await updateDoc(commentRef, {
        likes: Math.max(0, comment.likes - 1),
        likedBy: arrayRemove(user.id),
      });
    } else {
      await updateDoc(commentRef, {
        likes: comment.likes + 1,
        likedBy: arrayUnion(user.id),
      });
    }
  };

  const timeAgo = (timestamp) => {
    if (!timestamp) return 'maintenant';
    const seconds = Math.floor((new Date() - timestamp.toDate()) / 1000);
    if (seconds < 60) return 'maintenant';
    if (seconds < 3600) return Math.floor(seconds / 60) + ' min';
    if (seconds < 86400) return Math.floor(seconds / 3600) + ' h';
    return Math.floor(seconds / 86400) + ' j';
  };

  return (
    <div className="comments-section">
      <motion.button
        className="comments-toggle"
        onClick={() => setIsOpen(!isOpen)}
        whileTap={{ scale: 0.95 }}
      >
        <IoChatbubble />
        <span>Commentaires ({comments.length})</span>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="comments-panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            {replyTo && (
              <div className="comments-reply-banner">
                <span>↩ Réponse à <strong>{comments.find(c => c.id === replyTo)?.authorName || '...'}</strong></span>
                <button className="comments-reply-cancel" onClick={() => setReplyTo(null)}>✕</button>
              </div>
            )}

            {user ? (
              <form className="comments-form" onSubmit={handleSubmit}>
                <div className="comments-avatar">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <input
                  className="comments-input"
                  placeholder={replyTo ? 'Répondre au commentaire...' : 'Écrire un commentaire...'}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  maxLength={500}
                />
                <motion.button
                  type="submit"
                  className="comments-send"
                  disabled={!text.trim() || sending}
                  whileTap={{ scale: 0.85 }}
                >
                  <IoSend />
                </motion.button>
              </form>
            ) : (
              <div className="comments-login-prompt" onClick={() => window.location.href = '/auth'}>
                <span>🔒 <strong>Connecte-toi</strong> pour laisser un commentaire</span>
              </div>
            )}

            {comments.length > 1 && (
              <div className="comments-sort">
                <button
                  className={'comments-sort-btn ' + (sortBy === 'recent' ? 'comments-sort-active' : '')}
                  onClick={() => setSortBy('recent')}
                >
                  🕐 Récents
                </button>
                <button
                  className={'comments-sort-btn ' + (sortBy === 'popular' ? 'comments-sort-active' : '')}
                  onClick={() => setSortBy('popular')}
                >
                  ❤️ Populaires
                </button>
              </div>
            )}

            <div className="comments-list">
              {comments.length > 0 ? (
                [...comments].sort((a, b) => {
                  if (sortBy === 'popular') return (b.likes || 0) - (a.likes || 0);
                  return 0;
                }).map((comment, i) => {
                  const hasLiked = user && (comment.likedBy || []).includes(user.id);
                  return (
                    <motion.div
                      key={comment.id}
                      className="comment-item"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                    >
                      <div className="comment-avatar">
                        {comment.authorName.charAt(0).toUpperCase()}
                      </div>
                      <div className="comment-body">
                        <div className="comment-header">
                          <span className="comment-author">{comment.authorName}</span>
                          <span className="comment-time">{timeAgo(comment.createdAt)}</span>
                        </div>
                        {comment.replyToName && (
                          <p className="comment-reply-tag">↩ En réponse à {comment.replyToName}</p>
                        )}
                        <p className="comment-text">{comment.text}</p>
                        <div className="comment-actions">
                          <motion.button
                            className={'comment-like-btn ' + (hasLiked ? 'comment-like-btn-active' : '')}
                            onClick={() => handleLikeComment(comment)}
                            whileTap={{ scale: 0.8 }}
                          >
                            {hasLiked ? <IoHeart /> : <IoHeartOutline />}
                            {comment.likes > 0 && <span>{comment.likes}</span>}
                          </motion.button>
                          <button
                            className="comment-reply-btn"
                            onClick={() => {
                              if (!user) { window.location.href = '/auth'; return; }
                              setReplyTo(replyTo === comment.id ? null : comment.id);
                            }}
                          >
                            💬 Répondre
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              ) : (
                <p className="comments-empty">Sois le premier à commenter ce chapitre ! 🪶</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}